import {
  FC,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'

import usePopup, {
  PopupReturnType,
  PopupReturnTypeWithPayload
} from 'hooks/usePopup'
import { DeleteResponseUserPayload } from 'features/ContractDetails/ContractDetailsGeneral/ResponsibleUsers/DeleteUserPopup/types'
import { initialPopup } from '../../../hooks/constants'
import { deleteResponsibleUserPopupWithPayload } from '../../ContractDetails/Providers/constants'

type ContextProps = {
  deleteResponsibleUserPopup: PopupReturnTypeWithPayload<DeleteResponseUserPayload>
  addResponsibleUserPopup: PopupReturnType
  addHealthSystemPopup: PopupReturnType
  deleteHealthSystemsPopup: PopupReturnType
  addUsersPopup: PopupReturnType
  deleteUsersPopup: PopupReturnType
  editExpectedSavingsAndSpendPopup: PopupReturnType
}

const basePopup = (title: string) => ({
  title
})

const ContractCategoryDetailsPopupContext = createContext<ContextProps>({
  deleteResponsibleUserPopup: deleteResponsibleUserPopupWithPayload,
  addResponsibleUserPopup: initialPopup,
  addHealthSystemPopup: initialPopup,
  deleteHealthSystemsPopup: initialPopup,
  addUsersPopup: initialPopup,
  deleteUsersPopup: initialPopup,
  editExpectedSavingsAndSpendPopup: initialPopup
})

const ContractCategoryDetailsPopupProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const deleteResponsibleUserPopupProps = usePopup<DeleteResponseUserPayload>(
    basePopup('Delete team member?')
  )
  const addResponsibleUserPopupProps = usePopup(
    basePopup('Add team member to the contract category')
  )
  const addHealthSystemPopupProps = usePopup(basePopup('Add Health System'))
  const deleteHealthSystemsPopupProps = usePopup(
    basePopup('Delete Health System')
  )
  const addUsersPopupProps = usePopup(basePopup('Add users'))
  const deleteUsersPopupProps = usePopup(basePopup('Delete user'))
  const editExpectedSavingsAndSpendPopupProps = usePopup(
    basePopup('Edit Expected Savings and Spend')
  )

  const context = useMemo(
    () => ({
      deleteResponsibleUserPopup: deleteResponsibleUserPopupProps,
      addResponsibleUserPopup: addResponsibleUserPopupProps,
      addHealthSystemPopup: addHealthSystemPopupProps,
      deleteHealthSystemsPopup: deleteHealthSystemsPopupProps,
      addUsersPopup: addUsersPopupProps,
      deleteUsersPopup: deleteUsersPopupProps,
      editExpectedSavingsAndSpendPopup: editExpectedSavingsAndSpendPopupProps
    }),
    [
      deleteResponsibleUserPopupProps,
      addResponsibleUserPopupProps,
      addHealthSystemPopupProps,
      deleteHealthSystemsPopupProps,
      addUsersPopupProps,
      deleteUsersPopupProps,
      editExpectedSavingsAndSpendPopupProps
    ]
  )

  return (
    <ContractCategoryDetailsPopupContext.Provider value={context}>
      {children}
    </ContractCategoryDetailsPopupContext.Provider>
  )
}

export const useContractCategoryDetailsPopup = () =>
  useContext(ContractCategoryDetailsPopupContext)

export default ContractCategoryDetailsPopupProvider
